import { 
  PresignedUploadRequest, 
  PresignedUploadResponse, 
  FileUploadCompleteRequest,
  FileUploadResponse,
  TeamSubmissionsResponse,
  DownloadUrlResponse
} from './types';
import { apiGet, apiPost, apiDelete } from './api';

/**
 * File upload service for handling team file submissions
 */
export class FileUploadService {
  
  /**
   * Request a presigned URL for file upload
   */
  static async requestUploadUrl(
    teamId: string, 
    request: PresignedUploadRequest, 
    token: string
  ): Promise<PresignedUploadResponse> {
    return apiPost<PresignedUploadResponse>(
      `/teams/${teamId}/upload/presigned`, 
      request, 
      token
    );
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  static async uploadFileToS3(
    uploadUrl: string, 
    fields: Record<string, string>, 
    file: File
  ): Promise<void> {
    const formData = new FormData();
    
    // Add all the presigned URL fields first
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    
    // Add the file last (this is important for S3)
    formData.append('file', file);

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }
  }

  /**
   * Complete the upload process and create submission record
   */
  static async completeUpload(
    teamId: string, 
    request: FileUploadCompleteRequest, 
    token: string
  ): Promise<FileUploadResponse> {
    return apiPost<FileUploadResponse>(
      `/teams/${teamId}/upload/complete`, 
      request, 
      token
    );
  }

  /**
   * Get all submissions for a team
   */
  static async getTeamSubmissions(
    teamId: string, 
    token: string
  ): Promise<TeamSubmissionsResponse> {
    return apiGet<TeamSubmissionsResponse>(
      `/teams/${teamId}/submissions`, 
      token
    );
  }

  /**
   * Get download URL for a submission
   */
  static async getDownloadUrl(
    teamId: string, 
    submissionId: string, 
    token: string
  ): Promise<DownloadUrlResponse> {
    return apiGet<DownloadUrlResponse>(
      `/teams/${teamId}/submissions/${submissionId}/download`, 
      token
    );
  }

  /**
   * Delete a submission
   */
  static async deleteSubmission(
    teamId: string, 
    submissionId: string, 
    token: string
  ): Promise<void> {
    await apiDelete(`/teams/${teamId}/submissions/${submissionId}`, token);
  }

  /**
   * Upload file directly through the API (proxy method)
   */
  static async uploadFileDirect(
    teamId: string,
    file: File,
    submissionType: "document" | "video" | "photo",
    token: string,
    onProgress?: (stage: string, progress: number) => void
  ): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('submission_type', submissionType);

    onProgress?.("Uploading file...", 0);

    // Get API base URL from environment or default
    const API_BASE_URL = (window as any).REACT_APP_API_BASE_URL || 'http://localhost:8003';

    try {
      const response = await fetch(`${API_BASE_URL}/teams/${teamId}/upload/direct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      onProgress?.("Upload successful!", 100);
      return await response.json();
    } catch (error) {
      onProgress?.("Upload failed", 0);
      throw error;
    }
  }

  /**
   * Get upload configuration from the API
   */
  static async getUploadConfig(token: string): Promise<{
    upload_method: 'direct' | 'presigned';
    max_file_size: number;
    supported_methods: string[];
    description: Record<string, string>;
  }> {
    return apiGet('/teams/upload/config', token);
  }

  /**
   * Complete file upload flow from start to finish (presigned method)
   */
  static async uploadFilePresigned(
    teamId: string,
    file: File,
    submissionType: "document" | "video" | "photo",
    token: string,
    onProgress?: (stage: string, progress: number) => void
  ): Promise<FileUploadResponse> {
    try {
      // Stage 1: Request presigned URL
      onProgress?.("Preparing upload...", 0);
      
      const uploadRequest: PresignedUploadRequest = {
        filename: file.name,
        content_type: file.type,
        submission_type: submissionType
      };

      const presignedResponse = await this.requestUploadUrl(teamId, uploadRequest, token);
      onProgress?.("Starting upload...", 25);

      // Stage 2: Upload to S3
      await this.uploadFileToS3(
        presignedResponse.upload_url,
        presignedResponse.fields,
        file
      );
      onProgress?.("Upload complete, processing...", 75);

      // Stage 3: Complete upload
      const completeRequest: FileUploadCompleteRequest = {
        upload_id: presignedResponse.upload_id,
        file_size: file.size
      };

      const result = await this.completeUpload(teamId, completeRequest, token);
      onProgress?.("Upload successful!", 100);

      return result;
    } catch (error) {
      onProgress?.("Upload failed", 0);
      throw error;
    }
  }

  /**
   * Complete file upload flow from start to finish (auto-detect method)
   */
  static async uploadFile(
    teamId: string,
    file: File,
    submissionType: "document" | "video" | "photo",
    token: string,
    onProgress?: (stage: string, progress: number) => void
  ): Promise<FileUploadResponse> {
    try {
      // Get upload configuration
      const config = await this.getUploadConfig(token);
      
      // Choose upload method based on configuration
      if (config.upload_method === 'direct') {
        return await this.uploadFileDirect(teamId, file, submissionType, token, onProgress);
      } else {
        return await this.uploadFilePresigned(teamId, file, submissionType, token, onProgress);
      }
    } catch (error) {
      // Fallback to presigned method if config fetch fails
      console.warn('Failed to get upload config, falling back to presigned method:', error);
      return await this.uploadFilePresigned(teamId, file, submissionType, token, onProgress);
    }
  }

  /**
   * Validate file before upload
   */
  static validateFile(file: File, submissionType: "document" | "video" | "photo"): string | null {
    // Check file size based on submission type
    const maxFileSizes = {
      document: 100 * 1024 * 1024, // 100MB
      photo: 100 * 1024 * 1024,    // 100MB
      video: 500 * 1024 * 1024     // 500MB
    };

    const maxFileSize = maxFileSizes[submissionType];
    if (file.size > maxFileSize) {
      const maxSizeMB = maxFileSize / (1024 * 1024);
      return `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum allowed size (${maxSizeMB}MB) for ${submissionType} files`;
    }

    // Check file type based on submission type
    const allowedTypes: Record<string, string[]> = {
      document: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'text/plain'
      ],
      video: [
        'video/mp4',
        'video/avi',
        'video/x-msvideo',
        'video/quicktime'
      ],
      photo: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp'
      ]
    };

    const allowed = allowedTypes[submissionType] || [];
    if (!allowed.includes(file.type)) {
      return `File type ${file.type} is not allowed for ${submissionType} submissions. Allowed types: ${allowed.join(', ')}`;
    }

    return null; // No errors
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file type icon based on submission type
   */
  static getFileTypeIcon(submissionType: "document" | "video" | "photo"): string {
    const icons = {
      document: "📄",
      video: "🎥", 
      photo: "📸"
    };
    return icons[submissionType] || "📄";
  }
}
